import React from 'react';
import { Box } from '@chakra-ui/react';

import { getFormatDateFromStoreDate } from 'shared-components';
import { CourseLevelOfferStatus, ICourse, IImportCourse, ISummaryCourse } from 'types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryGroup } from '../../../Summary/SummaryGroup';
import { useTranslation } from "react-i18next";

interface Props {
  useImportCourses?: boolean;
  showConditionStatusInCourseDetails?: boolean;
  showCourseConditionsInCourseDetails?: boolean;
  studentNo?: string;
  courses?: Array<ICourse>;
  importCourses?: Array<IImportCourse>;
  summaryCourses?: ISummaryCourse[];
  riskList?: string[];
  showOfferEvidence?: boolean;
}

function CourseDetailsAndDeposit({
  studentNo,
  useImportCourses,
  showConditionStatusInCourseDetails,
  showCourseConditionsInCourseDetails,
  courses: allCourses,
  importCourses,
  showOfferEvidence,
  summaryCourses,
}: Props) {
    const { t } = useTranslation('forms.visa');
  const filteredCourses = allCourses?.filter(
    course => course && course.courseName !== undefined && course.courseName !== '',
  );
  const filteredImportCourses = importCourses?.filter(
    course => course && course.title !== undefined && course.title !== '',
  );
  const courses: Array<ICourse | IImportCourse> | undefined = useImportCourses
    ? filteredImportCourses
    : filteredCourses;

  return (
    <>
      {<SummaryRow className={'summary'} value={studentNo} label={t('forms.visa:studentNumber')} />}
      {courses !== undefined && <SummaryRow className={'summary'} value={courses?.length} label={t('forms.visa:howManyCourses')} />}
      {courses !== undefined && summaryCourses && (
        <Box as="ol" pl={8}>
          <SummaryGroup title={t('forms.visa:courses')}>
            {courses?.map((course: ICourse | IImportCourse, index: number) => {
              const importCourse = useImportCourses
                ? (course as IImportCourse)
                : importCourses?.find(importCourse => importCourse.title === course.courseName);
              const conditions = [
                importCourse?.conditionText1,
                importCourse?.conditionText2,
                importCourse?.conditionText3,
                importCourse?.conditionText4,
                importCourse?.conditionText5,
                importCourse?.conditionText6,
                importCourse?.conditionText7,
                importCourse?.conditionText8,
                importCourse?.conditionText9,
              ]
                .filter(Boolean)
                .join('\n')
                .trim();

              const getCourseOfferStatus = (c?: IImportCourse) => {
                if (c?.isDeclined) {
                  return CourseLevelOfferStatus.DECLINED;
                }
                if (c?.conditional) {
                  return CourseLevelOfferStatus.CONDITIONAL;
                }
                return CourseLevelOfferStatus.UNCONDITIONAL;
              };

              const offerStatus = getCourseOfferStatus(importCourse);

              const summaryCourse = summaryCourses[index];
              const fee = (course as ICourse).courseFee ?? (course as IImportCourse).courseFeeFirstYear;

              return (
                <React.Fragment key={`course-${index}`}>
                  <SummaryRow label={t('forms.visa:courseName')} value={summaryCourse.title} />

                  <SummaryRow label={t('forms.visa:startDate')} value={getFormatDateFromStoreDate(summaryCourse.startDate)} />

                  <SummaryRow label={t('forms.visa:endDate')} value={getFormatDateFromStoreDate(summaryCourse.endDate)} />

                  <SummaryRow label={t('forms.visa:fee')} value={fee} />

                  <SummaryRow label={t('forms.visa:isWorkPlacement')} value={summaryCourse.isWorkPlacement ? 'Yes' : 'No'} />

                  {course.lastAcceptanceDate && (
                    <SummaryRow
                      label={t('forms.visa:lastAcceptanceDate')}
                      value={getFormatDateFromStoreDate(course.lastAcceptanceDate)}
                    />
                  )}

                  {showOfferEvidence && course.offerEvidence && (
                    <SummaryRow
                      label={t('forms.visa:evidenceUsedForOffer')}
                      value={<Box whiteSpace={'pre-line'}>{course.offerEvidence}</Box>}
                    />
                  )}

                  {showConditionStatusInCourseDetails && <SummaryRow label={t('forms.visa:offerStatus')} value={offerStatus} />}

                  {showCourseConditionsInCourseDetails && Boolean(conditions) && (
                    <SummaryRow label={t('forms.visa:offerStatus')} value={<Box whiteSpace={'pre-line'}>{conditions}</Box>} />
                  )}
                </React.Fragment>
              );
            })}
          </SummaryGroup>
        </Box>
      )}
    </>
  );
}

export default CourseDetailsAndDeposit;
